import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Repeat, PlayCircle } from "lucide-react";
import { EXCHANGE_LIST } from '../ExchangeUIConfigs';

interface BasicSettingsProps {
  form: any;
  setForm: React.Dispatch<React.SetStateAction<any>>;
  row: (label: string, inputEl: React.ReactNode, helper?: string) => React.ReactNode;
  field: (key: string) => any;
  isReadOnly: boolean;
  isMarginReadOnly: boolean;
  isRunning: boolean;
  hasPosition: boolean;
  bot: any;
  currency: string;
}

export const BasicSettings: React.FC<BasicSettingsProps> = ({ 
  form, setForm, row, field, isReadOnly, isMarginReadOnly, isRunning, hasPosition, bot, currency 
}) => {
  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 ${isReadOnly ? 'opacity-60 pointer-events-none' : ''}`}>
      {row('거래소 (Exchange)', (
        <div className="w-full h-10 rounded-xl border border-border bg-muted/30 px-3 flex items-center">
          <span className="text-sm font-black text-foreground">
            {EXCHANGE_LIST.find(ex => ex.value === form.exchange)?.label || form.exchange}
          </span>
        </div>
      ), '거래소는 생성 후 변경할 수 없습니다.')}
      
      {row('봇 이름', <input disabled={isReadOnly} className="w-full h-10 rounded-xl border border-border px-3 text-sm font-black text-foreground focus:outline-none focus:ring-2 focus:ring-primary bg-background disabled:bg-muted/50" {...field('name')} />)}
      
      {row(form.is_live ? '실전 접속 환경' : '데이터 소스 (Price Feed)', (
        <div className="flex items-center justify-between w-full h-10 rounded-xl border border-border bg-muted/30 px-3">
          <span className="text-sm font-black text-foreground">
            {form.exchange === 'okx' 
              ? ((bot.settings || {})['use_testnet'] ? 'Demo Environment' : 'Mainnet Environment')
              : ((bot.settings || {})['use_testnet'] ? 'Testnet Environment' : 'Mainnet Environment')}
          </span>
          <Badge className={(bot.settings || {})['use_testnet'] ? 'bg-amber-500 text-white border-none' : 'bg-primary text-white border-none'}>
            {(bot.settings || {})['use_testnet'] ? (form.exchange === 'okx' ? 'DEMO' : 'TESTNET') : 'MAINNET'}
          </Badge>
        </div>
      ), form.is_live ? '실전 매매 시 사용할 거래소 서버 환경입니다.' : '모의 투자 시 가격 정보를 가져올 소스입니다.')}
      
      {row('매매 모드', (
        <div className="flex items-center justify-between w-full h-10 rounded-xl border border-border bg-muted/30 px-3">
          <span className="text-sm font-black text-foreground">{form.is_live ? 'Live Trading' : 'Simulation'}</span>
          <Badge className={!form.is_live ? 'bg-amber-500 text-white border-none' : 'bg-primary text-white border-none'}>{form.is_live ? 'LIVE' : 'PAPER'}</Badge>
        </div>
      ))}
      

      {row('진입 모드 (Margin Mode)', isMarginReadOnly ? (
        <div className="w-full h-10 rounded-xl border border-border bg-muted/30 px-3 flex items-center">
          <span className="text-sm font-black text-foreground">교차 (Cross)</span>
        </div>
      ) : (
        <div className="flex bg-muted/50 p-1 rounded-lg gap-1 w-full">
          <Button 
            variant={(form.margin_mode === 'crossed' || form.margin_mode === 'cross') ? "default" : "ghost"} 
            size="sm" 
            onClick={() => setForm({ ...form, margin_mode: (form.exchange === 'okx' ? 'cross' : 'crossed') })} 
            className={`flex-1 h-8 rounded-md px-4 text-[11px] font-bold transition-all ${(form.margin_mode === 'crossed' || form.margin_mode === 'cross') ? 'bg-primary text-white shadow-sm' : 'text-muted-foreground'}`}
          >
            교차 (Cross)
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            disabled
            className="flex-1 h-8 rounded-md px-4 text-[11px] font-bold text-muted-foreground/30 cursor-not-allowed bg-transparent opacity-40"
          >
            격리 (Isolated)
          </Button>
        </div>
      ), '봇 엔진 안정성 및 증거금 관리를 위해 현재 교차(Cross) 모드로만 고정되어 동작합니다.')}
      {row('레버리지', (bot.id && (isRunning || hasPosition)) ? (
        <div className="w-full h-10 rounded-xl border border-border bg-muted/30 px-3 flex items-center justify-between">
          <span className="text-sm font-black text-foreground">{form.leverage}x</span>
          <Badge variant="outline" className="text-[10px] font-bold border-border/50 text-muted-foreground">수정 불가</Badge>
        </div>
      ) : (
        <div className="space-y-1.5">
          <div className="w-full relative">
            <input type="number" step="1" min="1" className="w-full h-10 rounded-xl border border-border pl-3 pr-8 text-sm font-black text-foreground focus:outline-none focus:ring-2 focus:ring-primary bg-background" {...field('leverage')} />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">x</span>
          </div>
        </div>
      ), '포지션이 없고 정지 중인 상태에서만 레버리지를 수정할 수 있습니다.')}

      {row('캔들 타임프레임', (
        <select className="w-full h-10 rounded-xl border border-border px-3 text-sm font-black text-foreground focus:outline-none focus:ring-2 focus:ring-primary bg-background border-r-8 border-transparent" {...field('interval')}>
          <option value="1m">1분봉 (1m)</option>
          <option value="3m">3분봉 (3m)</option>
          <option value="5m">5분봉 (5m)</option>
          <option value="15m">15분봉 (15m)</option>
          <option value="30m">30분봉 (30m)</option>
          <option value="1h">1시간봉 (1h)</option>
        </select>
      ))}
      
      {row('사이클 자동 반복', (
        <div className="flex bg-muted/50 p-1 rounded-lg gap-1 w-full">
          <Button variant={form.cyclic ? "default" : "ghost"} size="sm" onClick={() => setForm({ ...form, cyclic: true })} 
            className={`flex-1 h-8 rounded-md px-4 text-[11px] font-bold transition-all ${form.cyclic ? 'bg-primary text-white shadow-sm' : 'text-muted-foreground'}`}>
            <Repeat className="w-3.5 h-3.5 mr-1.5" /> 반복 실행
          </Button>
          <Button variant={!form.cyclic ? "default" : "ghost"} size="sm" onClick={() => setForm({ ...form, cyclic: false })} 
            className={`flex-1 h-8 rounded-md px-4 text-[11px] font-bold transition-all ${!form.cyclic ? 'bg-muted-foreground/20 text-muted-foreground shadow-sm' : 'text-muted-foreground'}`}>
            <PlayCircle className="w-3.5 h-3.5 mr-1.5" /> 1회만 실행
          </Button>
        </div>
      ))}
      
      {row('운용 자산', (
        <div className={`flex bg-muted/50 p-1 rounded-lg gap-1 w-full ${isReadOnly ? 'opacity-50 pointer-events-none' : ''}`}>
          <Button disabled={isReadOnly} variant={!form.use_allocated_balance ? "default" : "ghost"} size="sm" 
            onClick={() => setForm({ ...form, use_allocated_balance: false })} 
            className={`flex-1 h-8 rounded-md px-4 text-[11px] font-bold transition-all ${!form.use_allocated_balance ? 'bg-muted-foreground/20 text-muted-foreground shadow-sm' : 'text-muted-foreground'}`}>전체 계좌</Button>
          <Button disabled={isReadOnly} variant={form.use_allocated_balance ? "default" : "ghost"} size="sm" 
            onClick={() => setForm({ ...form, use_allocated_balance: true })} 
            className={`flex-1 h-8 rounded-md px-4 text-[11px] font-bold transition-all ${form.use_allocated_balance ? 'bg-primary text-white shadow-sm' : 'text-muted-foreground'}`}>배정 금액만</Button>
        </div>
      ), '배정 금액 사용 시 설정된 금액 내에서만 운용합니다.')}
      
      {form.use_allocated_balance && row('배정 금액 (Balance)', (
        <div className="w-full relative">
          <input type="number" step="0.01" className="w-full h-10 rounded-xl border border-border pl-3 pr-10 text-sm font-black text-foreground focus:outline-none focus:ring-2 focus:ring-primary bg-background" {...field('balance')} />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">{currency}</span>
        </div>
      ))}

      {row('수수료 (Market %)', (
        <div className="w-full relative">
          <input type="number" step="0.001" className="w-full h-10 rounded-xl border border-border pl-3 pr-8 text-sm font-black text-foreground focus:outline-none focus:ring-2 focus:ring-primary bg-background" {...field('fee_pct')} />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">%</span>
        </div>
      ))}

      {row('수수료 (Limit %)', (
        <div className="w-full relative">
          <input type="number" step="0.001" className="w-full h-10 rounded-xl border border-border pl-3 pr-8 text-sm font-black text-foreground focus:outline-none focus:ring-2 focus:ring-primary bg-background" {...field('limit_fee_pct')} />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">%</span>
        </div>
      ))}
    </div>
  );
};
